import type { DayStr, HexColor, MockCache, Presence, User, UserId, UserRole } from '../../types';

import { snapshot } from '../internal';
import { logActivity } from '../journal';
import { bump, requireCache } from '../runtime';

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const IDENTITY_COLOR_COUNT = 8;

function initialsOf(displayName: string): string {
  return displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => Array.from(word)[0] ?? '')
    .join('')
    .toUpperCase();
}

function freeHandle(users: Iterable<User>, email: string): string {
  const base = (email.split('@')[0] ?? '').replace(/[^a-z0-9_]/g, '_');
  const taken = new Set(Array.from(users, (user) => user.handle));
  let handle = base;
  for (let suffix = 2; taken.has(handle); suffix += 1) {
    handle = `${base}${suffix}`;
  }
  return handle;
}

interface NewUserFields {
  id: UserId;
  kind: User['kind'];
  displayName: string;
  handle: string;
  jobTitle: string | null;
  email: string | null;
}

function insertUser(cache: MockCache, fields: NewUserFields): User {
  const now = cache.nowMs();
  const user: User = {
    id: fields.id,
    kind: fields.kind,
    role: 'member',
    displayName: fields.displayName,
    handle: fields.handle,
    avatarUrl: null,
    initials: initialsOf(fields.displayName),
    accentColor: `var(--identity-${(cache.users.size % IDENTITY_COLOR_COUNT) + 1})` as HexColor,
    jobTitle: fields.jobTitle,
    email: fields.email,
    about: null,
    aboutJson: null,
    contacts: {},
    birthdayOn: null,
    startedOn: new Date(Number(now)).toISOString().slice(0, 10) as DayStr,
    timezone: 'Europe/Moscow',
    workHours: { start: '10:00', end: '19:00', days: [1, 2, 3, 4, 5] },
    active: true,
  };
  cache.users.set(user.id, user);
  for (const workspace of cache.workspaces.values()) {
    if (!workspace.memberIds.includes(user.id)) {
      workspace.memberIds.push(user.id);
    }
  }
  const presence: Presence = {
    userId: user.id,
    state: 'offline',
    statusEmoji: null,
    statusText: null,
    statusUntilAt: null,
    lastSeenAt: now,
    typingInChannelId: null,
  };
  cache.presence.set(user.id, presence);
  logActivity(cache, {
    mutator: 'user.create',
    entityTable: 'user',
    entityId: String(user.id),
    op: 'insert',
    before: null,
    after: snapshot(user),
  });
  bump('workspace', 'presence');
  return user;
}

export function createUser(args: { id: UserId; email: string; displayName: string }): User {
  const cache = requireCache();
  const email = args.email.trim().toLowerCase();
  if (!EMAIL_SHAPE.test(email)) {
    throw new Error('Некорректный адрес почты');
  }
  for (const existing of cache.users.values()) {
    if (existing.email?.toLowerCase() === email) {
      throw new Error('Пользователь с такой почтой уже есть');
    }
  }
  const displayName = args.displayName.trim();
  if (displayName === '') {
    throw new Error('У человека должно быть имя');
  }
  return insertUser(cache, {
    id: args.id,
    kind: 'human',
    displayName,
    handle: freeHandle(cache.users.values(), email),
    jobTitle: null,
    email,
  });
}

export function createAgent(args: { id: UserId; handle: string; displayName: string; jobTitle: string | null }): User {
  const cache = requireCache();
  const handle = args.handle.trim().replace(/^@/, '').toLowerCase();
  if (handle === '') {
    throw new Error('У агента должен быть @handle');
  }
  if (/[^a-z0-9_]/.test(handle)) {
    throw new Error('@handle — только латиница, цифры и _');
  }
  for (const existing of cache.users.values()) {
    if (existing.handle === handle) {
      throw new Error('Такой @handle уже занят');
    }
  }
  const displayName = args.displayName.trim();
  if (displayName === '') {
    throw new Error('У агента должно быть имя');
  }
  return insertUser(cache, { id: args.id, kind: 'agent', displayName, handle, jobTitle: args.jobTitle, email: null });
}

export function setUserActive(args: { id: UserId; active: boolean }): void {
  const cache = requireCache();
  const user = cache.users.get(args.id);
  if (user === undefined) {
    throw new Error('Пользователь не найден');
  }
  if (!args.active && user.id === cache.me) {
    throw new Error('Нельзя отключить самого себя');
  }
  const before = { active: user.active };
  user.active = args.active;
  logActivity(cache, {
    mutator: 'user.setActive',
    entityTable: 'user',
    entityId: String(user.id),
    op: 'update',
    before,
    after: { active: user.active },
  });
  bump('workspace', 'presence');
}

export function setUserRole(args: { id: UserId; role: UserRole }): void {
  const cache = requireCache();
  const user = cache.users.get(args.id);
  if (user === undefined) {
    throw new Error('Пользователь не найден');
  }
  if (user.role === 'admin' && args.role !== 'admin') {
    const otherAdmins = Array.from(cache.users.values()).filter(
      (other) => other.id !== user.id && other.role === 'admin' && other.active,
    );
    if (otherAdmins.length === 0) {
      throw new Error('Нельзя снять роль с последнего администратора');
    }
  }
  const before = { role: user.role };
  user.role = args.role;
  logActivity(cache, {
    mutator: 'user.setRole',
    entityTable: 'user',
    entityId: String(user.id),
    op: 'update',
    before,
    after: { role: user.role },
  });
  bump('workspace');
}
